import React, { ReactNode, useState } from 'react';
import { Box, Button, ButtonProps, Flex, Text, useClipboard } from '@chakra-ui/react';
import { CaretDown, Copy } from 'phosphor-react';

import {
  AZUL,
  AZUL_ESCURO,
  Busca,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  Interruptor,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from '../permissoes/ui';
import { Tom, iniciais } from './mcpUtils';

// A tela de MCP usa as MESMAS peças visuais da Central de Permissões (cartões de vidro,
// rótulos em mono, pílulas, etiquetas, interruptores). Aqui só ficam os blocos pequenos
// que a Central ainda não tinha.
export {
  AZUL,
  Busca,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  Interruptor,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
};

export const BotaoPrimario = ({ children, ...props }: ButtonProps) => (
  <Button
    size="sm"
    borderRadius="999px"
    color="white"
    bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
    _hover={{ bg: AZUL_ESCURO }}
    {...props}
  >
    {children}
  </Button>
);

export const BotaoSecundario = ({ children, ...props }: ButtonProps) => (
  <Button size="sm" variant="outline" borderRadius="999px" bg="white" {...props}>
    {children}
  </Button>
);

export const BotaoPerigo = ({ children, ...props }: ButtonProps) => (
  <Button
    size="sm"
    variant="outline"
    borderRadius="999px"
    bg="white"
    color="#991B1B"
    borderColor="#FCA5A5"
    _hover={{ bg: '#FEE2E2' }}
    {...props}
  >
    {children}
  </Button>
);

/** Cabeçalho de bloco: rótulo mono, título grande e texto (como nos Logs de acesso). */
export const CabecaDeBloco = ({
  rotulo,
  titulo,
  texto,
  direita,
}: {
  rotulo: string;
  titulo: ReactNode;
  texto?: ReactNode;
  direita?: ReactNode;
}) => (
  <Flex align="flex-start" justify="space-between" gap="12px" wrap="wrap">
    <Box flex="1 1 320px" minW={0}>
      <Eyebrow>{rotulo}</Eyebrow>
      <Text fontSize="22px" lineHeight="28px" fontWeight={700} letterSpacing="-.3px" color={TEXTO}>
        {titulo}
      </Text>
      {texto && (
        <Text fontSize="13px" color="#475569">
          {texto}
        </Text>
      )}
    </Box>
    {direita}
  </Flex>
);

/** Número grande em mono com legenda: o que os Logs de acesso mostram à direita. */
export const Numero = ({
  valor,
  legenda,
  cor = TEXTO,
}: {
  valor: ReactNode;
  legenda: string;
  cor?: string;
}) => (
  <Box textAlign="right">
    <Text fontFamily={MONO} fontSize="22px" fontWeight={600} color={cor}>
      {valor}
    </Text>
    <Text fontSize="12px" color={TEXTO_SUAVE}>
      {legenda}
    </Text>
  </Box>
);

const FUNDO_AVISO: Record<'aviso' | 'perigo' | 'sucesso' | 'info', string[]> = {
  aviso: ['rgba(254,243,199,.6)', '#92400E'],
  perigo: ['rgba(254,226,226,.7)', '#991B1B'],
  sucesso: ['rgba(220,252,231,.7)', '#166534'],
  info: ['rgba(223,243,255,.8)', '#075985'],
};

/** Aviso em faixa, no estilo das mensagens de "pré-visualização" da Central. */
export const Aviso = ({
  tom = 'info',
  children,
}: {
  tom?: 'aviso' | 'perigo' | 'sucesso' | 'info';
  children: ReactNode;
}) => (
  <Text
    fontSize="12.5px"
    lineHeight="18px"
    color={FUNDO_AVISO[tom][1]}
    bg={FUNDO_AVISO[tom][0]}
    p="8px 12px"
    borderRadius="12px"
  >
    {children}
  </Text>
);

/** Bolinha com iniciais (a mesma dos usuários nos Logs de acesso). */
export const Bolinha = ({
  texto,
  tom = 'info',
}: {
  texto: string;
  tom?: 'info' | 'aviso' | 'neutro';
}) => {
  const cores = {
    info: ['#DFF3FF', '#075985'],
    aviso: ['#FEF3C7', '#92400E'],
    neutro: ['#F1F5F9', '#475569'],
  }[tom];
  return (
    <Flex
      w="32px"
      h="32px"
      borderRadius="50%"
      bg={cores[0]}
      color={cores[1]}
      align="center"
      justify="center"
      fontSize="11.5px"
      fontWeight={700}
      flex="none"
    >
      {iniciais(texto)}
    </Flex>
  );
};

/** Texto técnico (JSON, endereço, chave) em mono, com botão de copiar opcional. */
export const BlocoMono = ({
  children,
  copiar,
  maxH,
}: {
  children: string;
  copiar?: boolean;
  maxH?: string;
}) => {
  const { onCopy, hasCopied } = useClipboard(children);
  return (
    <Box position="relative">
      <Box
        as="pre"
        fontFamily={MONO}
        fontSize="12px"
        lineHeight="18px"
        color="#334155"
        bg="rgba(248,250,252,.9)"
        borderRadius="12px"
        boxShadow="inset 0 0 0 1px rgba(15,23,42,.07)"
        p="12px 14px"
        pr={copiar ? '84px' : '14px'}
        overflow="auto"
        maxH={maxH}
        whiteSpace="pre-wrap"
        wordBreak="break-all"
      >
        {children}
      </Box>
      {copiar && (
        <Button
          size="xs"
          variant="outline"
          bg="white"
          borderRadius="999px"
          position="absolute"
          top="8px"
          right="8px"
          leftIcon={<Copy size={12} />}
          onClick={onCopy}
        >
          {hasCopied ? 'Copiado' : 'Copiar'}
        </Button>
      )}
    </Box>
  );
};

/** Linha de lista com a divisória fina dos Logs de acesso. */
export const Linha = ({ children, ...props }: React.ComponentProps<typeof Flex>) => (
  <Flex
    align="center"
    gap="14px"
    p="10px 20px"
    borderTop="1px solid rgba(15,23,42,.05)"
    wrap="wrap"
    {...props}
  >
    {children}
  </Flex>
);

const TONS_ICONE: Record<Tom, [string, string]> = {
  info: ['#DFF3FF', '#075985'],
  aviso: ['#FEF3C7', '#92400E'],
  sucesso: ['#DCFCE7', '#166534'],
  neutro: ['#F1F5F9', '#475569'],
  perigo: ['#FEE2E2', '#991B1B'],
};

/** Ícone dentro de um círculo colorido: o estado se lê pela cor e pelo desenho. */
export const IconeRedondo = ({
  icone,
  tom = 'info',
  tamanho = 36,
}: {
  icone: ReactNode;
  tom?: Tom;
  tamanho?: number;
}) => (
  <Flex
    w={`${tamanho}px`}
    h={`${tamanho}px`}
    borderRadius="50%"
    bg={TONS_ICONE[tom][0]}
    color={TONS_ICONE[tom][1]}
    align="center"
    justify="center"
    flex="none"
  >
    {icone}
  </Flex>
);

/** Cartão de estado: ícone, rótulo curto e UMA palavra de estado. */
export const Ladrilho = ({
  icone,
  rotulo,
  valor,
  tom,
  dica,
}: {
  icone: ReactNode;
  rotulo: string;
  valor: string;
  tom: Tom;
  dica?: string;
}) => (
  <Cartao
    p="14px 16px"
    flex="1 1 190px"
    minW={0}
    display="flex"
    alignItems="center"
    gap="12px"
    title={dica}
  >
    <IconeRedondo icone={icone} tom={tom} />
    <Box minW={0}>
      <Text fontSize="12px" color={TEXTO_SUAVE}>
        {rotulo}
      </Text>
      <Text fontSize="16px" fontWeight={700} color={TEXTO} lineHeight="20px">
        {valor}
      </Text>
    </Box>
  </Cartao>
);

/** Bloco que começa fechado: o detalhe só aparece para quem quer ver. */
export const Recolhivel = ({
  icone,
  titulo,
  resumo,
  abertoInicial = false,
  children,
}: {
  icone: ReactNode;
  titulo: string;
  resumo?: string;
  abertoInicial?: boolean;
  children: ReactNode;
}) => {
  const [aberto, setAberto] = useState(abertoInicial);
  return (
    <Cartao p={0} overflow="hidden">
      <Flex
        as="button"
        type="button"
        w="100%"
        align="center"
        gap="12px"
        p="14px 18px"
        textAlign="left"
        aria-expanded={aberto}
        onClick={() => setAberto(v => !v)}
        _hover={{ bg: 'rgba(255,255,255,.6)' }}
      >
        <IconeRedondo icone={icone} tom="neutro" tamanho={32} />
        <Box flex={1} minW={0}>
          <Text fontSize="14px" fontWeight={700} color={TEXTO}>
            {titulo}
          </Text>
          {resumo && (
            <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
              {resumo}
            </Text>
          )}
        </Box>
        <Box
          color={TEXTO_SUAVE}
          transform={aberto ? 'rotate(180deg)' : undefined}
          transition="transform .2s"
        >
          <CaretDown size={16} />
        </Box>
      </Flex>
      {aberto && (
        <Flex direction="column" gap="10px" p="4px 18px 18px">
          {children}
        </Flex>
      )}
    </Cartao>
  );
};

export type { Tom };
